/**
 * Per-project stack inventory (PLAN G10 / V30): components, technologies, services, accounts,
 * dev workflows, agent skills and required tools. Sealed as one `inventory` entity per project
 * (the relay only sees an opaque head). Credentials appear only as `op://` references; values
 * never enter the inventory.
 */
import { z } from 'zod';
import { TimestampMs } from './common.js';
export const INVENTORY_CATEGORIES = [
    'component',
    'technology',
    'service',
    'account',
    'workflow',
    'skill',
    'tool',
];
export const InventoryCategory = z.enum(INVENTORY_CATEGORIES);
/** Who asserted the entry: the daemon scanner, an agent (MCP), or the user. */
export const InventorySource = z.enum(['scan', 'agent', 'user']);
/** Stable per-project key, `<category>:<slug>` (e.g. `technology:react`). */
export const InventoryKey = z
    .string()
    .regex(/^[a-z]+:[a-z0-9@][a-z0-9._@/+:-]{0,119}$/, 'must be <category>:<lowercase slug>');
/** Same shape as `viko.project.yaml` refs: `op://vault/item/[section/]field`. */
export const InventoryOpRef = z
    .string()
    .max(512)
    .regex(/^op:\/\/[^/\s](?:[^/\r\n]*[^/\s])?(?:\/[^/\s](?:[^/\r\n]*[^/\s])?){2,3}$/, 'must be a 1Password reference op://vault/item/[section/]field');
/**
 * `project`: a vault the daemon's 1Password service account may read (non-interactive).
 * `personal`: the user's own vault; read only through the 1Password desktop app (Touch ID),
 * never with the service account.
 */
export const InventoryVault = z.enum(['project', 'personal']);
export const InventoryCredential = z.strictObject({
    env: z.string().regex(/^[A-Z_][A-Z0-9_]*$/),
    ref: InventoryOpRef,
    vault: InventoryVault,
});
const Text = (max) => z.string().trim().min(1).max(max);
/** Readiness of a required tool/service on the scanning machine (doctor-backed). */
export const InventoryStatus = z.strictObject({
    state: z.enum(['ready', 'missing', 'unverified']),
    summary: Text(300),
    /** One-click setup guidance: a command to copy/run and/or a docs link. */
    fix: z
        .strictObject({
        text: Text(300),
        command: Text(300).optional(),
        url: z.url().max(512).optional(),
    })
        .optional(),
    machineId: Text(128).optional(),
    checkedAt: TimestampMs,
});
export const InventoryEntry = z.strictObject({
    key: InventoryKey,
    category: InventoryCategory,
    name: Text(120),
    version: Text(64).optional(),
    detail: Text(1000).optional(),
    /** Repo-relative paths where the entry was found (drill-down). */
    paths: z.array(Text(300)).max(12).optional(),
    /** Sub-items (e.g. a component's dependencies), shown on drill-down. */
    items: z.array(Text(120)).max(60).optional(),
    tags: z.array(Text(40)).max(12).optional(),
    url: z.url().max(512).optional(),
    /** Needed to develop/maintain/ship the project (doctor checks it). */
    required: z.boolean().optional(),
    credentials: z.array(InventoryCredential).max(8).optional(),
    status: InventoryStatus.optional(),
    source: InventorySource,
    /** Agent entries: the run that asserted it. */
    runId: Text(128).optional(),
    updatedAt: TimestampMs,
});
/** One brokered secret use (never the value). */
export const InventoryAccess = z.strictObject({
    ts: TimestampMs,
    runId: Text(128),
    ref: InventoryOpRef,
    command: Text(120),
    vault: InventoryVault,
    outcome: z.enum(['ok', 'failed', 'denied', 'pending_approval']),
    /** Value came from the run's batch unlock (no new 1Password prompt). */
    cached: z.boolean().optional(),
    exitCode: z.number().int().optional(),
});
export const INVENTORY_MAX_ENTRIES = 400;
export const INVENTORY_MAX_ACCESS = 50;
/** Sealed `inventory` entity value (one per project). */
export const ProjectInventory = z.strictObject({
    version: z.literal(1),
    projectId: Text(128),
    updatedAt: TimestampMs,
    scannedAt: TimestampMs.optional(),
    machineId: Text(128).optional(),
    entries: z.array(InventoryEntry).max(INVENTORY_MAX_ENTRIES),
    /** Most recent brokered secret uses, newest first. The full log stays on the machine. */
    access: z.array(InventoryAccess).max(INVENTORY_MAX_ACCESS).optional(),
});
/** What an agent may assert through `viko_inventory_update` (source/run/time are set by vikod). */
export const InventoryAgentEntry = z.strictObject({
    category: InventoryCategory,
    name: Text(120),
    /** Defaults to `<category>:<slugified name>`. */
    key: InventoryKey.optional(),
    version: Text(64).optional(),
    detail: Text(1000).optional(),
    paths: z.array(Text(300)).max(12).optional(),
    items: z.array(Text(120)).max(60).optional(),
    tags: z.array(Text(40)).max(12).optional(),
    url: z.url().max(512).optional(),
    required: z.boolean().optional(),
    credentials: z.array(InventoryCredential).max(8).optional(),
});
export function inventorySlug(name) {
    return (name
        .toLowerCase()
        .replace(/[^a-z0-9._@/+-]+/g, '-')
        .replace(/^[^a-z0-9@]+|-+$/g, '')
        .slice(0, 100) || 'item');
}
export function inventoryKey(category, name) {
    return `${category}:${inventorySlug(name)}`;
}
// --- MCP tools --------------------------------------------------------------------------------
export const VikoInventoryInput = z.object({
    category: InventoryCategory.optional(),
    /** Case-insensitive substring over key/name/detail/tags. */
    query: z.string().max(200).optional(),
});
export const VikoInventoryOutput = z.object({
    scannedAt: TimestampMs.optional(),
    entries: z.array(InventoryEntry),
});
export const VikoInventoryUpdateInput = z
    .object({
    upsert: z.array(InventoryAgentEntry).max(50).optional(),
    /** Keys of agent-asserted entries to drop (scanned/user entries cannot be removed). */
    remove: z.array(InventoryKey).max(50).optional(),
})
    .refine((v) => (v.upsert?.length ?? 0) + (v.remove?.length ?? 0) > 0, 'nothing to update');
export const VikoInventoryUpdateOutput = z.object({
    ok: z.literal(true),
    upserted: z.array(InventoryKey),
    removed: z.array(InventoryKey),
});
//# sourceMappingURL=inventory.js.map